import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Filter, ArrowUpCircle, ArrowDownCircle, Printer, Search, Pencil } from "lucide-react";
import SelectiveBillDialog from "@/components/SelectiveBillDialog";
import PaymentModeEditor from "@/components/PaymentModeEditor";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const PRESETS = [
  { label: "Today", from: today(), to: today() },
  { label: "This Week", from: daysAgo(6), to: today() },
  { label: "This Month", from: new Date().toISOString().slice(0, 7) + "-01", to: today() },
  { label: "Last 30 Days", from: daysAgo(29), to: today() },
  { label: "All Time", from: "", to: "" },
];

export default function Billing() {
  const [sp] = useSearchParams();
  const [pays, setPays] = useState([]);
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [mode, setMode] = useState("");
  const [type, setType] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", kind: "credit", category: "Consultation", description: "", amount: 0, mode: "Cash", notes: "" });
  const [rxOpen, setRxOpen] = useState(false);            // Print picker dialog
  const [rxOpdId, setRxOpdId] = useState("");
  const [rxOpds, setRxOpds] = useState([]);
  const [rxQ, setRxQ] = useState("");
  const [editingPay, setEditingPay] = useState(null);   // payment being mode-edited

  const load = () => {
    const params = {};
    if (from && to) { params.from_date = from; params.to_date = to; }
    if (mode) params.mode = mode;
    if (type) params.type = type;
    api.get("/payments", { params }).then((r) => setPays(r.data));
    api.get("/bill-items").then((r) => setItems(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, mode, type]);
  useEffect(() => { api.get("/patients").then((r) => setPatients(r.data)); }, []);

  // Consume ?patient=<id> from Ctrl+B shortcut → open New Transaction dialog pre-filled.
  useEffect(() => {
    const pid = sp.get("patient") || "";
    if (pid) {
      setForm((f) => ({ ...f, patient_id: pid }));
      setOpen(true);
    }
  }, [sp]);

  const total = pays.filter(p => p.type === "payment").reduce((a, b) => a + b.amount, 0);
  const adv = pays.filter(p => p.type === "advance").reduce((a, b) => a + b.amount, 0);
  const ref = pays.filter(p => p.type === "refund").reduce((a, b) => a + b.amount, 0);
  const modes = {};
  pays.filter(p => p.type === "payment").forEach(p => { modes[p.mode] = (modes[p.mode] || 0) + p.amount; });

  const submit = async () => {
    if (!form.patient_id || !form.amount) return toast.error("Patient and amount required");
    try {
      await api.post("/transactions", form);
      toast.success(`${form.kind === "debit" ? "Debit (charge)" : "Credit (payment)"} added`);
      setOpen(false);
      setForm({ patient_id: "", kind: "credit", category: "Consultation", description: "", amount: 0, mode: "Cash", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold">Billing & Payments</h1>
        <div className="flex gap-2">
          <Button data-testid="print-receipt-btn" onClick={async () => {
            setRxOpen(true); setRxOpdId(""); setRxQ("");
            try {
              const { data } = await api.get("/opd");
              setRxOpds(data || []);
            } catch { setRxOpds([]); }
          }} variant="outline" className="rounded-sm"><Printer className="w-4 h-4 mr-1"/> Print Custom Receipt</Button>
          <Button data-testid="new-transaction-btn" onClick={() => setOpen(true)} className="bg-blue-700 hover:bg-blue-800 rounded-sm"><Plus className="w-4 h-4 mr-1"/> New Transaction</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border p-3 flex flex-wrap gap-2 items-center">
        <Filter className="w-4 h-4 text-slate-500" />
        <label className="label-tiny">From</label>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="bill-from" />
        <label className="label-tiny">To</label>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="bill-to" />
        <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-9 border text-sm px-2"><option value="">All Modes</option>{["Cash","UPI","PMJAY","PACKAGE","GPay","Card","Bank"].map((m) => <option key={m}>{m}</option>)}</select>
        <select value={type} onChange={(e) => setType(e.target.value)} className="h-9 border text-sm px-2"><option value="">All Types</option><option value="payment">Payment</option><option value="advance">Advance</option><option value="refund">Refund</option></select>
        <div className="flex gap-1 flex-wrap">
          {PRESETS.map((p) => (
            <button key={p.label} onClick={() => { setFrom(p.from); setTo(p.to); }} className={`text-xs px-2 py-1 border rounded-sm ${from === p.from && to === p.to ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 hover:border-blue-500"}`}>{p.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border p-3"><div className="label-tiny">Total Collection</div><div className="font-mono text-lg mt-1">{fmtINR(total)}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Advance</div><div className="font-mono text-lg mt-1 text-teal-700">{fmtINR(adv)}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Refund</div><div className="font-mono text-lg mt-1 text-red-700">{fmtINR(ref)}</div></div>
        <div className="bg-white border p-3"><div className="label-tiny">Transactions</div><div className="font-mono text-lg mt-1">{pays.length}</div></div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {Object.entries(modes).map(([k, v]) => (
          <div key={k} className="bg-white border p-3"><div className="label-tiny">{k}</div><div className="font-mono mt-1">{fmtINR(v)}</div></div>
        ))}
      </div>

      <div className="bg-white border">
        <div className="p-3 border-b label-tiny">Recent Transactions</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Receipt</th><th className="label-tiny">Date</th><th className="label-tiny">Type</th><th className="label-tiny">Mode</th><th className="label-tiny">Notes</th><th className="label-tiny text-right">Amount</th><th className="label-tiny text-center w-32">Print</th></tr></thead>
          <tbody>
            {pays.map((p) => {
              const targetId = p.opd_id || p.ipd_id;
              const kind = p.opd_id ? "opd" : (p.ipd_id ? "ipd" : "");
              const canPrint = !!targetId;
              const printUrl = (layout) => `/bill/${kind}/${targetId}/print?layout=${layout}`;
              return (
                <tr key={p.id} className="border-b hover:bg-slate-50" data-testid={`bill-row-${p.receipt_no || p.id}`}>
                  <td className="p-2 font-mono">{p.receipt_no}</td>
                  <td className="text-xs">{fmtDT(p.created_at)}</td>
                  <td>{p.type}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <span className="font-semibold text-xs uppercase">{p.mode}</span>
                      {p.type === "payment" && !p.is_void && (
                        <button
                          data-testid={`edit-mode-${p.receipt_no || p.id}`}
                          onClick={() => setEditingPay(p)}
                          className="p-0.5 text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 rounded-sm transition"
                          title="Change payment mode (audit-logged)">
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="text-xs text-slate-600">{p.notes}</td>
                  <td className="text-right font-mono">{fmtINR(p.amount)}</td>
                  <td className="text-center">
                    {canPrint ? (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => window.open(printUrl("a4"), "_blank")}
                          className="text-[10px] px-2 py-1 border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-sm font-semibold"
                          title="Print Full A4 Bill"
                          data-testid={`bill-print-a4-${p.receipt_no || p.id}`}>A4</button>
                        <button
                          onClick={() => window.open(printUrl("thermal58"), "_blank")}
                          className="text-[10px] px-2 py-1 border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-sm font-semibold"
                          title="Print 58mm Thermal Receipt"
                          data-testid={`bill-print-58-${p.receipt_no || p.id}`}>58mm</button>
                        <button
                          onClick={() => window.open(printUrl("thermal80"), "_blank")}
                          className="text-[10px] px-2 py-1 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-sm"
                          title="Print 80mm Thermal Receipt"
                          data-testid={`bill-print-80-${p.receipt_no || p.id}`}>80</button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {pays.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-sm text-slate-500">No transactions in the selected range</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>New Transaction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <button data-testid="txn-credit" onClick={() => setForm({ ...form, kind: "credit" })} className={`flex-1 border p-3 rounded-sm ${form.kind === "credit" ? "border-teal-600 bg-teal-50" : "border-slate-300"}`}>
                <ArrowDownCircle className="w-6 h-6 text-teal-700 mx-auto"/>
                <div className="text-xs mt-1 font-semibold">CREDIT (Received Payment)</div>
                <div className="text-[10px] text-slate-500">Money received from patient</div>
              </button>
              <button data-testid="txn-debit" onClick={() => setForm({ ...form, kind: "debit" })} className={`flex-1 border p-3 rounded-sm ${form.kind === "debit" ? "border-red-600 bg-red-50" : "border-slate-300"}`}>
                <ArrowUpCircle className="w-6 h-6 text-red-700 mx-auto"/>
                <div className="text-xs mt-1 font-semibold">DEBIT (Charge)</div>
                <div className="text-[10px] text-slate-500">Add charge against patient bill</div>
              </button>
            </div>
            <div>
              <Label className="label-tiny">Patient</Label>
              <select data-testid="txn-patient" value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} className="w-full h-9 border px-2 text-sm">
                <option value="">-- Select patient --</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.uhid} · {p.mobile}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-tiny">Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-9 border px-2 text-sm">
                  {form.kind === "debit" ? ["Consultation","Room","Doctor Visit","Medicine","Lab","Investigation","OT","Surgery","Implant","Other"].map((c) => <option key={c}>{c}</option>) : ["Consultation","IPD Bill","OPD","Advance","Refund","Other"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="label-tiny">Amount ₹</Label>
                <Input type="number" data-testid="txn-amount" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} />
              </div>
              {form.kind === "credit" && (
                <div>
                  <Label className="label-tiny">Payment Mode</Label>
                  <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full h-9 border px-2 text-sm">
                    {["Cash", "UPI", "PMJAY", "PACKAGE", "GPay", "Card", "Bank"].map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div>
              <Label className="label-tiny">Description / Notes</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="txn-save" onClick={submit} className={form.kind === "debit" ? "bg-red-700 hover:bg-red-800" : "bg-teal-700 hover:bg-teal-800"}>Save {form.kind === "debit" ? "Debit" : "Credit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Custom Receipt · pick OPD → open SelectiveBillDialog */}
      <Dialog open={rxOpen && !rxOpdId} onOpenChange={setRxOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Pick an OPD to Print Receipt</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
              <Input value={rxQ} onChange={(e) => setRxQ(e.target.value)} placeholder="Search by OPD # / UHID / patient / doctor" className="pl-7 h-9" data-testid="bill-rx-search"/>
            </div>
            <div className="border rounded-sm max-h-[380px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0"><tr>
                  <th className="text-left label-tiny px-2 py-1.5">OPD #</th>
                  <th className="text-left label-tiny px-2 py-1.5">Patient</th>
                  <th className="text-left label-tiny px-2 py-1.5">Doctor</th>
                  <th className="text-left label-tiny px-2 py-1.5">Date</th>
                  <th className="text-right label-tiny px-2 py-1.5 w-16">Pick</th>
                </tr></thead>
                <tbody>
                  {(rxOpds || [])
                    .filter((r) => {
                      const t = rxQ.trim().toLowerCase();
                      if (!t) return true;
                      return (r.opd_no||"").toLowerCase().includes(t) || (r.uhid||"").toLowerCase().includes(t) || (r.patient_name||"").toLowerCase().includes(t) || (r.doctor_name||"").toLowerCase().includes(t);
                    })
                    .slice(0, 40)
                    .map((r) => (
                    <tr key={r.id} className="border-t hover:bg-blue-50 cursor-pointer" data-testid={`bill-rx-row-${r.opd_no}`} onClick={() => setRxOpdId(r.id)}>
                      <td className="px-2 py-1.5 font-mono text-xs">{r.opd_no}</td>
                      <td className="px-2 py-1.5"><div className="font-medium">{r.patient_name}</div><div className="text-[10px] text-slate-500 font-mono">{r.uhid}</div></td>
                      <td className="px-2 py-1.5 text-xs">{r.doctor_name}</td>
                      <td className="px-2 py-1.5 text-xs text-slate-500">{fmtDT(r.created_at)}</td>
                      <td className="px-2 py-1.5 text-right text-[11px] text-blue-700 underline">Pick</td>
                    </tr>
                  ))}
                  {(rxOpds || []).length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-xs text-slate-500">No OPDs found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SelectiveBillDialog
        open={rxOpen && !!rxOpdId}
        onOpenChange={(o) => { if (!o) { setRxOpdId(""); setRxOpen(false); } }}
        opdId={rxOpdId}
      />

      {editingPay && (
        <PaymentModeEditor
          payment={editingPay}
          onClose={() => setEditingPay(null)}
          onSaved={() => { setEditingPay(null); load(); }}
        />
      )}
    </div>
  );
}
